import { createFileRoute, Link, redirect, useNavigate } from "@tanstack/react-router";
import { useState, type SubmitEvent } from "react";
import { AuthShell } from "@/components/auth-shell";
import { ErrorText } from "@/components/page";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { errorMessage } from "@/lib/api";
import { recoverAccount, session } from "@/lib/session";

export const Route = createFileRoute("/recover")({
  beforeLoad: () => {
    if (session.get().status !== "anon") throw redirect({ to: "/" });
  },
  component: RecoverPage,
});

function RecoverPage() {
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [key, setKey] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const onSubmit = async (e: SubmitEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    try {
      await recoverAccount(email, key, password, setBusy);
      void nav({ to: "/" });
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(null);
    }
  };

  return (
    <AuthShell
      title="Recover your account"
      description="Enter the recovery key you saved at signup to set a new password. Other browsers will be signed out."
      footer={
        <Link to="/login" className="underline underline-offset-4">
          Back to sign in
        </Link>
      }
    >
      <form onSubmit={(e) => void onSubmit(e)} className="space-y-4">
        <div className="space-y-1.5">
          <Label htmlFor="email">Email</Label>
          <Input
            id="email"
            type="email"
            required
            autoComplete="username"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="key">Recovery key</Label>
          <Input
            id="key"
            required
            placeholder="ES-XXXX-XXXX-…"
            className="font-mono"
            value={key}
            onChange={(e) => setKey(e.target.value)}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="password">New password</Label>
          <Input
            id="password"
            type="password"
            required
            autoComplete="new-password"
            minLength={10}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </div>
        <ErrorText error={error} />
        <Button type="submit" className="w-full" disabled={busy !== null}>
          {busy ?? "Reset password"}
        </Button>
      </form>
    </AuthShell>
  );
}
